import { Injectable, OnDestroy } from '@angular/core';

/** Callback de sesión (logout/continuar): puede ser síncrono o async. */
export interface SessionTimeoutHandler {
  (): void | Promise<void>;
}

@Injectable({ providedIn: 'root' })
export class SessionTimeoutService implements OnDestroy {
  private static readonly IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutos
  private static readonly WARNING_BEFORE_MS = 15 * 1000; // 15 segundos de advertencia

  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private warningDialog: HTMLElement | null = null;
  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private onLogoutHandler: SessionTimeoutHandler | null = null;
  private onContinueHandler: SessionTimeoutHandler | null = null;

  private events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

  setOnLogoutHandler(handler: SessionTimeoutHandler): void {
    this.onLogoutHandler = handler;
  }

  /** Se ejecuta al pulsar "Continuar sesión" (p. ej. refresh del token). */
  setOnContinueHandler(handler: SessionTimeoutHandler): void {
    this.onContinueHandler = handler;
  }

  start(): void {
    this.stop();
    this.resetIdleTimer();
    this.events.forEach((ev) => window.addEventListener(ev, this.onActivity));
  }

  stop(): void {
    this.clearTimers();
    this.closeWarning();
    this.events.forEach((ev) => window.removeEventListener(ev, this.onActivity));
  }

  private onActivity = (): void => {
    if (this.warningDialog) return;
    this.resetIdleTimer();
  };

  private resetIdleTimer(): void {
    this.clearTimers();
    this.idleTimer = setTimeout(
      () => this.showWarning(),
      SessionTimeoutService.IDLE_TIMEOUT_MS - SessionTimeoutService.WARNING_BEFORE_MS
    );
  }

  private showWarning(): void {
    if (this.warningDialog) return;

    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed; inset: 0; z-index: 99999;
      background: rgba(0,0,0,0.5);
      display: flex; align-items: center; justify-content: center;
      font-family: 'Inter', -apple-system, sans-serif;
    `;
    overlay.id = 'sf-timeout-overlay';

    let countdown = 15;

    overlay.innerHTML = `
      <div style="
        background: #0d1f15; border-radius: 16px; padding: 2rem 2.5rem;
        max-width: 400px; width: 90%; text-align: center;
        box-shadow: 0 20px 60px rgba(0,0,0,0.5);
        border: 1px solid rgba(74,222,128,0.2);
      ">
        <div style="font-size: 2.5rem; margin-bottom: 0.75rem;">⏱️</div>
        <h2 style="color:#fff; margin:0 0 0.5rem; font-size:1.1rem; font-weight:700;">
          Sesión por expirar
        </h2>
        <p style="color:rgba(255,255,255,0.6); font-size:0.85rem; margin:0 0 1.25rem; line-height:1.4;">
          Su sesión se cerrará automáticamente por inactividad.
        </p>
        <div style="
          font-size: 2rem; font-weight: 800; color: #4ade80;
          margin-bottom: 1.5rem; font-variant-numeric: tabular-nums;
        " id="sf-countdown">${countdown}</div>
        <div style="display:flex; gap:0.75rem; justify-content:center;">
          <button id="sf-btn-continue" style="
            flex:1; padding:0.6rem 1rem; border:none; border-radius:8px;
            background:#22c55e; color:#fff; font-size:0.85rem; font-weight:700;
            cursor:pointer;
          ">Continuar sesión</button>
          <button id="sf-btn-logout" style="
            padding:0.6rem 1rem; border:none; border-radius:8px;
            background:rgba(255,255,255,0.08); color:rgba(255,255,255,0.6);
            font-size:0.85rem; font-weight:600; cursor:pointer;
          ">Cerrar</button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    this.warningDialog = overlay;

    this.countdownInterval = setInterval(() => {
      countdown--;
      const el = document.getElementById('sf-countdown');
      if (el) el.textContent = String(countdown);
      if (countdown <= 0) {
        this.clearTimers();
        this.closeWarning();
        this.onLogoutHandler?.();
      }
    }, 1000);

    overlay.querySelector('#sf-btn-continue')?.addEventListener('click', () => {
      this.clearTimers();
      this.closeWarning();
      this.resetIdleTimer();
      this.onContinueHandler?.();
    });

    overlay.querySelector('#sf-btn-logout')?.addEventListener('click', () => {
      this.clearTimers();
      this.closeWarning();
      this.onLogoutHandler?.();
    });
  }

  private closeWarning(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    const overlay = document.getElementById('sf-timeout-overlay');
    if (overlay) overlay.remove();
    this.warningDialog = null;
  }

  private clearTimers(): void {
    if (this.idleTimer) { clearTimeout(this.idleTimer); this.idleTimer = null; }
  }

  ngOnDestroy(): void {
    this.stop();
  }
}